import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { Connection } from '../obj/Connection';
import { Flow } from '../obj/Flow';
import { FlowRun } from '../obj/FlowRun';
import { EnvScanSource, StorageBackend, StorageMode, StoredEnvironment, StoreStats } from './storage-backend';
import { ConfigurationItem, UserItem } from './storage-types';

@Injectable({
  providedIn: 'root',
})
export class StorageNoCacheService implements StorageBackend {
  private configs = new Map<string, ConfigurationItem>();
  private environments = new Map<string, StoredEnvironment>();
  private flowsSubject = new BehaviorSubject<Flow[]>([]);
  private flowsAdminSubject = new BehaviorSubject<Flow[]>([]);
  private runsSubject = new BehaviorSubject<FlowRun[]>([]);
  private usersSubject = new BehaviorSubject<UserItem[]>([]);
  private connectionsSubject = new BehaviorSubject<Connection[]>([]);

  getMode(): StorageMode {
    return 'no-cache';
  }

  async flush(): Promise<void> {}

  async getConfig(key: string): Promise<ConfigurationItem> {
    return this.configs.get(key) || { key, value: null };
  }

  async putConfig(key: string, item: ConfigurationItem): Promise<any> {
    this.configs.set(key, { key, value: item?.value ?? null });
    return true;
  }

  async getFlows(): Promise<Flow[]> {
    return this.flowsSubject.value;
  }

  async getFlowSummaries(environment?: string): Promise<Flow[]> {
    const flows = this.flowsSubject.value;
    return environment ? flows.filter((flow: any) => flow.environment === environment) : flows;
  }

  async getFlowsAdmin(): Promise<Flow[]> {
    return this.flowsAdminSubject.value;
  }

  getFlows$() {
    return this.flowsSubject.asObservable();
  }

  getFlowsAdmin$() {
    return this.flowsAdminSubject.asObservable();
  }

  async putFlow(flow: Flow, admin = false): Promise<any> {
    return this.putFlows([flow], admin);
  }

  async putFlows(flows: Flow[], admin = false): Promise<any> {
    const subject = admin ? this.flowsAdminSubject : this.flowsSubject;
    subject.next(flows || []);
    return true;
  }

  async removeFlows(flows: Flow[], admin = false): Promise<any> {
    const removeKeys = new Set((flows || []).map((flow: any) => `${flow.environment || ''}/${flow.flowName || flow._id || ''}`));
    const subject = admin ? this.flowsAdminSubject : this.flowsSubject;
    subject.next(subject.value.filter((flow: any) => !removeKeys.has(`${flow.environment || ''}/${flow.flowName || flow._id || ''}`)));
    return true;
  }

  async markRunScanQueued(_environment: string, _flowNames: string[]): Promise<any> {
    return true;
  }

  async clearRunScanQueued(_environment: string, _flowNames: string[]): Promise<any> {
    return true;
  }

  async putRuns(runs: FlowRun[]): Promise<any> {
    this.runsSubject.next(runs || []);
    return true;
  }

  getRuns$(_environment?: string | null) {
    return this.runsSubject.asObservable();
  }

  async getUsers(): Promise<UserItem[]> {
    return this.usersSubject.value;
  }

  getUsers$() {
    return this.usersSubject.asObservable();
  }

  async getUserByEmail(email: string): Promise<UserItem | undefined> {
    const needle = String(email || '').toLowerCase();
    return this.usersSubject.value.find((user: any) =>
      String(user.email || '').toLowerCase() === needle ||
      (Array.isArray(user.emails) && user.emails.some((alias: string) => String(alias || '').toLowerCase() === needle)));
  }

  async putUsers(users: UserItem[]): Promise<any> {
    this.usersSubject.next(users || []);
    return true;
  }

  async getConnections(environment?: string): Promise<Connection[]> {
    const connections = this.connectionsSubject.value;
    return environment ? connections.filter((connection: any) => connection.environment === environment) : connections;
  }

  getConnections$() {
    return this.connectionsSubject.asObservable();
  }

  async putConnections(connections: Connection[]): Promise<any> {
    this.connectionsSubject.next(connections || []);
    return true;
  }

  async getEnvironment(name: string): Promise<StoredEnvironment | null> {
    return this.environments.get(name) || null;
  }

  async getEnvironments(): Promise<StoredEnvironment[]> {
    return [...this.environments.values()];
  }

  async putEnvironment(env: any): Promise<any> {
    const stored = this.toStoredEnvironment(env);
    this.environments.set(stored.name, stored);
    return true;
  }

  async putEnvironments(envs: any[]): Promise<any> {
    for (const env of envs || []) {
      const stored = this.toStoredEnvironment(env);
      this.environments.set(stored.name, stored);
    }
    return true;
  }

  async markEnvScanCompleted(name: string, flowCount: number, durationMs: number): Promise<any> {
    const env = this.environments.get(name);
    if (env) {
      env.scanCompletedAt = Date.now();
      env.flowCount = flowCount;
      env.durationMs = durationMs;
    }
    return true;
  }

  async markEnvSourceScanned(name: string, source: EnvScanSource, count?: number, durationMs?: number): Promise<any> {
    const env = this.environments.get(name);
    if (!env) return true;
    if (source === 'flows') {
      env.scanCompletedAt = Date.now();
      env.flowCount = count ?? env.flowCount;
      env.durationMs = durationMs ?? env.durationMs;
    } else if (source === 'flowsAdmin') {
      env.flowsAdminScannedAt = Date.now();
    } else if (source === 'connections') {
      env.connectionsScannedAt = Date.now();
    } else {
      env.connectionsAdminScannedAt = Date.now();
    }
    return true;
  }

  async getStoreStats(): Promise<StoreStats> {
    return {
      fileSizeBytes: null,
      tables: {
        configs: this.configs.size,
        flows: this.flowsSubject.value.length,
        flows_admin: this.flowsAdminSubject.value.length,
        runs: this.runsSubject.value.length,
        users: this.usersSubject.value.length,
        user_aliases: 0,
        environments: this.environments.size,
        connections: this.connectionsSubject.value.length,
      },
    };
  }

  async nukeStore(): Promise<void> {
    this.configs.clear();
    this.environments.clear();
    this.flowsSubject.next([]);
    this.flowsAdminSubject.next([]);
    this.runsSubject.next([]);
    this.usersSubject.next([]);
    this.connectionsSubject.next([]);
  }

  async getAllConfigs(): Promise<{ key: string; value: any }[]> {
    return [...this.configs.values()].map((item) => ({ key: item.key, value: item.value }));
  }

  async getAllUserAliases(): Promise<{ email: string; user_key: string }[]> {
    return [];
  }

  private toStoredEnvironment(env: any): StoredEnvironment {
    return {
      name: env?.name || env?.id || '',
      displayName: env?.displayName || env?.properties?.displayName || env?.name || '',
      json: env,
      scanCompletedAt: null,
      flowCount: null,
      durationMs: null,
      flowsAdminScannedAt: null,
      connectionsScannedAt: null,
      connectionsAdminScannedAt: null,
    };
  }
}
